import m, {type ComponentType, type RouteResolver, getSearch} from '@bitstillery/mithril'
import {make_route, make_route_conditional, optional_auth, require_auth, require_no_auth} from '@bitstillery/common/lib/router'
import {PortalDialogShell} from '@bitstillery/common/components/portal'
import {$s} from '@bitstillery/common/app'

import {AuthInvite, MyPortal, MyPortalEmail, OfferList, Checkout} from './components/pages'
import {DialogAuth} from '@/components/auth/dialog_auth'
import {embed_in_content_frame} from '@/cms/components/shell/content_frame/content_frame'
import {LayoutPortal} from './components/layout'

import {CmsDynamicPage} from '@/cms/runtime/page'
import {CmsDiscoverPreviewFrame} from '@/cms/runtime/preview'
import {ready_cms_page} from '@/cms/runtime/prefetch'
import {PORTAL_CMS_CATCH_ALL_ROUTE} from '@/cms/lib/route_utils'
import {LEGACY_PATHS, legacy_portal_query, legacy_portal_target} from '@/lib/legacy_routes'
import {FavouriteList} from '@/components/pages/offers/favourites/list'

const with_auth = make_route(require_auth, LayoutPortal)
const opt_auth_portal = make_route(optional_auth, LayoutPortal)

export const DEFAULT_ROUTE = '/'

/**
 * The published-CMS route, held until its page is ready to be shown in one piece.
 *
 * Mithril keeps the current route rendered while `onmatch` is pending, which is the only moment where
 * waiting is free: the shop (or wherever the visitor came from) stays on screen instead of being torn
 * down into an empty `<main>` while the CMS page fetches, compiles and fills in section by section.
 * `ready_cms_page` does that work, bounded by its own deadline, so a slow page delays
 * the swap by at most that — and a warm page passes straight through.
 *
 * Auth resolves first: a redirect must not wait on a page it will never show.
 */
const cms_resolver = opt_auth_portal(CmsDynamicPage)

const cms_page_route: RouteResolver = {
    ...cms_resolver,
    // Not async: `RouteResolver.onmatch` is typed as a union of promises rather than a promise of a
    // union, which an async function's return type doesn't fit.
    onmatch: (args, requested_path, route) => {
        const matched = cms_resolver.onmatch?.(args, requested_path, route)
        if (matched === CmsDynamicPage) {
            const ready: Promise<ComponentType> = ready_cms_page(requested_path).then(() => CmsDynamicPage)
            return ready
        }
        return matched
    },
}

/**
 * Render `Overlay` on top of the CMS home page, which stays mounted behind it. Used for the
 * route-driven dialogs (auth, invite): the URL stays a real, refreshable, deep-linkable
 * route while the home page shows through (CmsDynamicPage loads '/' for overlay routes). `Overlay`
 * is responsible for its own dialog chrome.
 */
function overlay_home(Overlay: ComponentType<Record<string, unknown>>) {
    return {view: () => [m(CmsDynamicPage), m(Overlay)]}
}

/** Wrap a bare page component in the shared portal dialog chrome (closing returns home). */
// Mithril types a child vnode's attrs as a record, so `m(Inner)` only fits as a child with record attrs.
function as_overlay_dialog(Inner: ComponentType<Record<string, unknown>>) {
    return {view: () => m(PortalDialogShell, {}, m(Inner))}
}

/** Overlay route on the home page, gated to logged-out visitors (authenticated → home). */
const home_overlay_route = (Overlay: ComponentType<Record<string, unknown>>) =>
    make_route(require_no_auth, LayoutPortal)(overlay_home(Overlay))

/**
 * Auth routes (login / register / forgot-password / reset-password) share one resolver so
 * navigating between them patches the tree instead of remounting it — the dialog and the home
 * page behind it stay mounted. The dialog itself derives its active tab/sub-view from the URL.
 */
const auth_page = home_overlay_route(DialogAuth)

/**
 * One redirect per `LEGACY_PATHS` entry, forwarding with the query intact — that query is what
 * the link was for: the shop filters, `show_popup_of`, the utm tags. It is read with `getSearch()`
 * rather than from the router's path, because SSR resolves routes on the pathname alone and the query
 * only ever reaches it on the request URL.
 *
 * Registered ahead of the real routes (see the spread below), so that a broader pattern further down
 * cannot quietly swallow one — `/my-portal/:path...` matches `/my-portal/profile` perfectly well and
 * would render a tab-less My Portal instead of redirecting.
 */
const legacy_routes = Object.fromEntries(
    Object.entries(LEGACY_PATHS).map(([was, target]) => [
        was,
        {
            onmatch: (params: Record<string, string>) =>
                m.route.redirect(`${legacy_portal_target(target, params)}${legacy_portal_query(getSearch())}`),
        },
    ]),
)

export const routes = {
    ...legacy_routes,
    '/invite/:code': home_overlay_route(as_overlay_dialog(AuthInvite)),
    '/login': auth_page,
    '/register': auth_page,
    '/forgot-password': auth_page,
    '/reset-password': auth_page,
    '/my-portal/email': make_route_conditional(
        () =>
            !$s.identity.token && !$s.identity.user.artkey
                ? embed_in_content_frame(MyPortalEmail)
                : embed_in_content_frame(MyPortal),
        () => LayoutPortal,
    ),
    '/my-portal': with_auth({
        oninit: () => {
            void m.route.set('/my-portal/orders')
        },
        view: () => null,
    }),
    '/my-portal/:path...': with_auth(embed_in_content_frame(MyPortal)),

    // The shop hero is collapsed, so this copy surfaces only as the document title + meta description
    // (see cms/lib/page_meta.ts) — it is still the shop's own copy, not the vendor fallback.
    // @i18n include: shell.title.shop
    // @i18n include: shell.description.shop
    [`/shop`]: with_auth(OfferList),
    // @i18n include: breadcrumbs.favourites
    ['/shop/favourites']: with_auth(FavouriteList),
    [`/shop/checkout`]: with_auth(Checkout),

    /** Discover CMS editor iframe — postMessage preview (same-origin runtime bundle). */
    '/cms/preview': opt_auth_portal(CmsDiscoverPreviewFrame),

    [PORTAL_CMS_CATCH_ALL_ROUTE]: cms_page_route,
}
